import React, { useState, useEffect, useCallback } from 'react';
import { Clock, Plus, Edit2, Trash2, X, Loader2, Save, CheckCircle2 } from 'lucide-react';
import api from '../services/api';
import { uyar, hata, onayla } from '../components/GlobalModal';

interface Period {
  periodID: number;
  periodName: string;
  startTime: string;
  endTime: string;
  maxPerPerson: number;
  price: number;
  sortOrder: number;
  isActive: boolean;
}

const fmtTime = (t?: string) => {
  if (!t) return '';
  const m = String(t).match(/(\d{1,2}):(\d{2})/);
  return m ? `${m[1].padStart(2, '0')}:${m[2]}` : String(t).substring(0, 5);
};
const maskHHmm = (v: string) => {
  const digits = v.replace(/\D/g, '').substring(0, 4);
  if (digits.length <= 2) return digits;
  return `${digits.substring(0, 2)}:${digits.substring(2)}`;
};
const isValidHHmm = (v: string) => /^([01]\d|2[0-3]):[0-5]\d$/.test(v);

const MealPeriods: React.FC = () => {
  const [rows, setRows] = useState<Period[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<Period | null>(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    periodName: '',
    startTime: '',
    endTime: '',
    maxPerPerson: 1,
    price: 0,
    sortOrder: 0,
    isActive: true,
  });

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r: any = await api.get('/meals/periods');
      const raw = r?.data ?? r;
      const arr = Array.isArray(raw) ? raw : (Array.isArray(raw?.data) ? raw.data : []);
      setRows(arr);
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Öğünler yüklenemedi');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setEditing(null);
    setForm({ periodName: '', startTime: '', endTime: '', maxPerPerson: 1, price: 0, sortOrder: rows.length + 1, isActive: true });
    setShowModal(true);
  };
  const openEdit = (p: Period) => {
    setEditing(p);
    setForm({
      periodName: p.periodName,
      startTime: fmtTime(p.startTime),
      endTime: fmtTime(p.endTime),
      maxPerPerson: p.maxPerPerson,
      price: p.price,
      sortOrder: p.sortOrder ?? 0,
      isActive: p.isActive,
    });
    setShowModal(true);
  };

  const save = async () => {
    if (!form.periodName.trim()) { uyar('Öğün adı zorunlu'); return; }
    if (!isValidHHmm(form.startTime)) { uyar('Başlangıç saati HH:mm formatında olmalı'); return; }
    if (!isValidHHmm(form.endTime)) { uyar('Bitiş saati HH:mm formatında olmalı'); return; }
    setSaving(true); setError(null);
    try {
      const body = {
        periodName: form.periodName,
        startTime: `${form.startTime}:00`,
        endTime: `${form.endTime}:00`,
        maxPerPerson: Number(form.maxPerPerson) || 1,
        price: Number(form.price) || 0,
        sortOrder: Number(form.sortOrder) || 0,
        isActive: form.isActive,
      };
      if (editing) {
        await api.put(`/meals/periods/${editing.periodID}`, body);
      } else {
        await api.post('/meals/periods', body);
      }
      setShowModal(false);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Kaydedilemedi');
    } finally { setSaving(false); }
  };

  const remove = async (p: Period) => {
    if (!(await onayla(`"${p.periodName}" silinsin mi?`))) return;
    try {
      await api.delete(`/meals/periods/${p.periodID}`);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Silinemedi');
    }
  };

  return (
    <div className="p-6 md:p-8 space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-black text-slate-800 flex items-center gap-2">
            <Clock size={26} className="text-blue-600" /> Öğün Tanımları
          </h1>
          <p className="text-sm text-slate-500 mt-1">Kahvaltı, öğle, akşam ve diğer öğün tanımları</p>
        </div>
        <button onClick={openNew}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl text-sm font-bold hover:bg-blue-700">
          <Plus size={16} /> Yeni Öğün
        </button>
      </div>

      {error && <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">{error}</div>}

      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-[10px] font-black text-slate-500 uppercase tracking-wider">
                <th className="px-4 py-3">Sıra</th>
                <th className="px-4 py-3">Öğün Adı</th>
                <th className="px-4 py-3">Başlangıç</th>
                <th className="px-4 py-3">Bitiş</th>
                <th className="px-4 py-3">Kişi Başı Max</th>
                <th className="px-4 py-3">Fiyat</th>
                <th className="px-4 py-3">Durum</th>
                <th className="px-4 py-3 text-right">İşlem</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="px-4 py-16 text-center text-slate-400">
                  <Loader2 size={20} className="animate-spin mx-auto" />
                </td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-16 text-center text-slate-400">
                  <Clock size={32} className="mx-auto opacity-30 mb-2" /> Tanımlı öğün yok
                </td></tr>
              ) : rows.map(p => (
                <tr key={p.periodID} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-3 text-slate-600">{p.sortOrder ?? '—'}</td>
                  <td className="px-4 py-3 font-bold text-slate-800">{p.periodName}</td>
                  <td className="px-4 py-3 font-mono text-slate-700">{fmtTime(p.startTime)}</td>
                  <td className="px-4 py-3 font-mono text-slate-700">{fmtTime(p.endTime)}</td>
                  <td className="px-4 py-3">
                    <span className="inline-flex px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 text-[11px] font-bold">Max {p.maxPerPerson}</span>
                  </td>
                  <td className="px-4 py-3 font-bold text-slate-700">{Number(p.price ?? 0).toFixed(2)} ₺</td>
                  <td className="px-4 py-3">
                    {p.isActive ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[11px] font-bold">
                        <CheckCircle2 size={10} /> Aktif
                      </span>
                    ) : (
                      <span className="inline-flex px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 text-[11px] font-bold">Pasif</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => openEdit(p)} className="mr-2 text-blue-600 hover:text-blue-800"><Edit2 size={14} /></button>
                    <button onClick={() => remove(p)} className="text-red-600 hover:text-red-800"><Trash2 size={14} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowModal(false)}>
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between bg-blue-600 text-white px-6 py-4 rounded-t-2xl">
              <h3 className="font-black text-lg">{editing ? 'Öğün Düzenle' : 'Yeni Öğün'}</h3>
              <button onClick={() => setShowModal(false)} className="hover:bg-white/20 rounded p-1"><X size={18} /></button>
            </div>
            <div className="p-6 space-y-3">
              <div>
                <label className="block text-xs font-bold text-slate-600 mb-1">Öğün Adı *</label>
                <input type="text" value={form.periodName} onChange={e => setForm({ ...form, periodName: e.target.value })}
                  placeholder="Kahvaltı / Öğle Yemeği / Akşam Yemeği"
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">Başlangıç (HH:mm)</label>
                  <input type="text" value={form.startTime} onChange={e => setForm({ ...form, startTime: maskHHmm(e.target.value) })}
                    placeholder="06:00" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm font-mono" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">Bitiş (HH:mm)</label>
                  <input type="text" value={form.endTime} onChange={e => setForm({ ...form, endTime: maskHHmm(e.target.value) })}
                    placeholder="08:30" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm font-mono" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">Kişi Başı Max</label>
                  <input type="number" min={1} value={form.maxPerPerson} onChange={e => setForm({ ...form, maxPerPerson: Number(e.target.value) || 1 })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">Fiyat (₺)</label>
                  <input type="number" step="0.01" min={0} value={form.price} onChange={e => setForm({ ...form, price: Number(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-600 mb-1">Sıra</label>
                <input type="number" min={0} value={form.sortOrder} onChange={e => setForm({ ...form, sortOrder: Number(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input type="checkbox" checked={form.isActive} onChange={e => setForm({ ...form, isActive: e.target.checked })} className="w-4 h-4" />
                Aktif
              </label>
            </div>
            <div className="flex items-center justify-end gap-3 border-t border-slate-100 p-4">
              <button onClick={() => setShowModal(false)} className="px-4 py-2 rounded-lg border border-slate-200 text-sm font-bold text-slate-700 hover:bg-slate-50">İptal</button>
              <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-bold hover:bg-blue-700 disabled:opacity-60">
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Kaydet
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MealPeriods;
